import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { AdfParser, AdfRepairStrategy, AdfSchemaValidator } from '@atlassian/adf-parser';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';

import type {
	StreamErrorCallback,
	StreamRepairCallback,
} from '../command-handlers/command-handler';
import type { ContentLocation } from '../types/content';
import { log } from '../utils/aifc-logging';

export class DocumentCreateCommandProcessor {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private contentLocation: ContentLocation;
	private actionId: string | undefined;
	private accumulated: string = '';
	private parser: AdfParser | undefined;

	constructor(
		editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>,
		contentLocation: ContentLocation,
	) {
		this.editorApi = editorApi;
		this.contentLocation = contentLocation;
	}

	private init() {
		this.editorApi.aiStreamingOrchestrator?.actions.startSession();

		this.actionId = fg('platform_editor_ai_aifc_patch_beta')
			? this.editorApi.aiStreamingOrchestrator?.actions.initAction(
					{
						type: 'appendNode',
						streamingType: 'create',
					},
					{
						/**
						 * Always hide the loading screen as staging area always shows Rovo conversation
						 * and will already have a loading indicator.
						 */
						hideLoadingScreen: this.contentLocation === 'staging-area',
					},
				)
			: this.editorApi.aiStreamingOrchestrator?.actions.initAction({
					type: 'appendNode',
					streamingType: 'create',
				});
		if (!this.actionId) {
			throw new Error('Failed to initialize streaming action for create');
		}

		const schema = this.editorApi.core.sharedState.currentState()?.schema;
		if (schema) {
			this.parser = new AdfParser({
				schemaValidator: new AdfSchemaValidator(schema),
				repairStrategy: new AdfRepairStrategy(schema),
				config: {
					closeBrokenContent: fg('platform_editor_ai_improved_table_parsing'),
					lenientStringParsing: fg('platform_editor_ai_lenient_string_parsing'),
				},
			});
		}

		log('Initialized editor streaming action', { streamType: 'create', actionId: this.actionId });
	}

	stream(adf: string, isFinished: boolean = false) {
		if (!this.actionId) {
			this.init();
		}

		if (!adf || !this.actionId) {
			return;
		}

		if (this.parser) {
			this.parser.processChunk(adf, isFinished);
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(
				// NOTE: This is a temporary solution before we move the streamer inside `/platform`
				this.actionId,
				JSON.stringify(this.parser.getPartialDocument()) ?? '',
			);
		} else {
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, adf);
		}

		this.accumulated += adf;
		log('Streaming content', { actionId: this.actionId, adf });
	}

	async finish({
		onError,
		onRepair,
	}: {
		onError?: StreamErrorCallback;
		onRepair?: StreamRepairCallback;
	}) {
		if (this.actionId) {
			this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
			await this.editorApi?.aiStreamingOrchestrator?.actions.endSession();
			log('Finished action', { actionId: this.actionId, fullAdf: this.accumulated });

			let finalDoc: DocNode | undefined;
			try {
				const schema = this.editorApi.core.sharedState.currentState()?.schema;
				if (!schema) {
					throw new Error('Schema is not available');
				}
				const parser = new AdfParser({
					schemaValidator: new AdfSchemaValidator(schema),
					repairStrategy: new AdfRepairStrategy(schema),
					config: {
						closeBrokenContent: fg('platform_editor_ai_improved_table_parsing'),
						lenientStringParsing: fg('platform_editor_ai_lenient_string_parsing'),
					},
				});
				const doc = parser.parse(this.accumulated);

				const newStreamer = new ADFStreamer(schema);
				newStreamer.processChunk(JSON.stringify(doc || {}));
				finalDoc = newStreamer.getLastDoc();

				const repairs = parser.getRepairs();
				if (repairs.length > 0) {
					onRepair?.({ repairs, streamType: 'create' });
				}
			} catch (error) {
				log('New ADF Parser failed, falling back to ADF Streamer', { error });
				onError?.({ error, streamType: 'create' });
				// Give ADF Streamer a chance to repair the ADF
				const schema = this.editorApi.core.sharedState.currentState()?.schema;
				if (schema) {
					const newStreamer = new ADFStreamer(schema);
					newStreamer.processChunk(this.accumulated);
					finalDoc = newStreamer.getLastDoc();
				}
			}

			this.accumulated = '';
			this.actionId = undefined;

			return finalDoc;
		}
	}

	abort() {
		this.editorApi.aiStreamingOrchestrator?.actions.abortSession();
	}
}
